import { useUpdateTicketOptimistically } from "@jugl-web/domain-resources/tickets/hooks/useUpdateTicketOptimistically";
import { UpdateTicketPayload } from "@jugl-web/rest-api";
import { DelayedRender, LoadingAnimation } from "@jugl-web/ui-components";
import { useMe } from "@web-src/features/app/hooks/useMe";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { useTicketsLimit } from "@web-src/modules/client-support/hooks/useTicketsLimit";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useTicketsViewContext } from "../../../../providers/TicketsViewProvider";
import {
  ClientSupportTicketsView,
  NewTicketFormValues,
} from "../../../../types";
import { TicketsEmptyState } from "../TicketsEmptyState";
import { TicketsLoadingErrorState } from "../TicketsLoadingErrorState";
import { TicketsNoResultsState } from "../TicketsNoResultsState";
import { TicketsTableSkeleton } from "../TicketsTableSkeleton";
import { TicketsViewTable } from "../TicketsViewTable";
import { onBeforeRefresh$ } from "../TicketsViewTopBar/components/TicketsViewRefreshButton";

interface TicketsViewContentProps {
  ticketsView: ClientSupportTicketsView;
}

export const TicketsViewContent: FC<TicketsViewContentProps> = ({
  ticketsView,
}) => {
  const { entityId } = useEntitySelectedProvider();
  const { id: meId = "" } = useMe();

  const { openCreateTicketSidebar } = useClientSupportPageContext();
  const { ticketListQuery, searchQuery, sort } = useTicketsViewContext();
  const { isTicketCreationBlocked } = useTicketsLimit();

  const {
    tickets,
    hasMore,
    isLoading,
    isLoadingMore,
    isError,
    loadMore,
    retry,
    setTickets,
  } = ticketListQuery;

  const { updateTicket } = useUpdateTicketOptimistically({
    entityId,
    setTickets,
  });

  const { ref: sentinelRef, inView } = useInView();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleOpenPrefilledNewTicketForm = useCallback(() => {
    let initialFormValues: Partial<NewTicketFormValues> = {};

    switch (ticketsView.filter) {
      case "assigned_to_user":
        initialFormValues = { assigneeIds: [meId] };
        break;
      case "in_progress":
      case "on_customer":
      case "on_hold":
      case "issued":
        initialFormValues = { status: ticketsView.filter };
        break;
      default:
        break;
    }

    openCreateTicketSidebar(initialFormValues);
  }, [ticketsView.filter, meId, openCreateTicketSidebar]);

  const produceUpdateTicketFn = useCallback(
    (ticketId: string) => (data: UpdateTicketPayload) =>
      updateTicket(ticketId, data),
    [updateTicket]
  );

  // Load more tickets when the sentinel is in view
  useEffect(() => {
    if (inView && hasMore && !isLoadingMore) {
      loadMore();
    }
  }, [inView, hasMore, isLoadingMore, loadMore]);

  // Reset scroll position when list query parameters change.
  useLayoutEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [ticketsView.filter, searchQuery, sort]);

  // Reset scroll position before refreshing the list
  useEffect(() => {
    onBeforeRefresh$.subscribe(() => {
      scrollContainerRef.current?.scrollTo({ top: 0 });
    });
  }, []);

  if (isLoading) {
    return (
      <DelayedRender
        isLoading
        loadingContent={
          <div className="flex h-full w-full animate-fade-in flex-col">
            <TicketsTableSkeleton />
          </div>
        }
      />
    );
  }

  if (isError) {
    return (
      <div className="flex h-full animate-fade-in items-center justify-center">
        <TicketsLoadingErrorState onRetry={retry} />
      </div>
    );
  }

  const isEmpty = tickets.length === 0;

  if (isEmpty) {
    return (
      <div
        key="empty-state"
        className="flex h-full animate-fade-in items-center justify-center"
      >
        {searchQuery.length > 0 ? (
          <TicketsNoResultsState />
        ) : (
          <TicketsEmptyState
            variant={ticketsView.filter}
            onCreateTicket={handleOpenPrefilledNewTicketForm}
            isCreateBlocked={isTicketCreationBlocked}
          />
        )}
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      className="jugl__custom-scrollbar flex h-full w-full animate-fade-in flex-col overflow-auto"
    >
      <TicketsViewTable
        entityId={entityId}
        tickets={tickets}
        produceUpdateTicketFn={produceUpdateTicketFn}
      />

      {hasMore && !isLoadingMore && (
        <div
          ref={sentinelRef}
          className="flex items-center justify-center pt-2 pb-4"
        >
          <LoadingAnimation size="lg" />
        </div>
      )}
    </div>
  );
};
